import type { DailyCapacity } from '@/domains/calendar/utils/capacityCalculator';
import type { Job } from '@/domains/jobs/components/JobCard';
import { addDays, differenceInDays, format, parseISO } from 'date-fns';
import { AlertTriangle } from 'lucide-react';
import { useRef, useState } from 'react';
import { useDrop } from 'react-dnd';
import { GanttJobBar } from './GanttJobBar';

interface GanttResourceRowProps {
  resourceId: string;
  resourceName: string;
  jobs: Job[]; // Jobs assigned to this resource
  rangeStart: Date; // Start date of visible range
  pixelsPerDay: number;
  totalDays: number;
  onDrop: (job: Job, resourceId: string, date: Date) => void;
  onJobClick?: (jobId: string) => void;
  dailyCapacity?: Map<string, DailyCapacity>; // Client-side calculated capacity
  conflicts?: Map<string, Job[][]>; // Scheduling conflicts
  zoomLevel?: 'hour' | 'day' | 'week' | 'month';
}

export function GanttResourceRow({
  resourceId,
  resourceName,
  jobs,
  rangeStart,
  pixelsPerDay,
  totalDays,
  onDrop,
  onJobClick,
  dailyCapacity,
  conflicts,
  zoomLevel = 'week',
}: GanttResourceRowProps) {
  const timelineRef = useRef<HTMLDivElement>(null);
  const [hoverDayIndex, setHoverDayIndex] = useState<number | null>(null);
  const [hoverJob, setHoverJob] = useState<Job | null>(null);

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: 'JOB',
    canDrop: (item: Job, monitor) => {
      const offset = monitor.getClientOffset();
      if (!offset || !timelineRef.current) return false;

      const timelineBounds = timelineRef.current.getBoundingClientRect();
      const relativeX = offset.x - timelineBounds.left;
      const dayIndex = Math.floor(relativeX / pixelsPerDay);
      const clampedDayIndex = Math.max(0, Math.min(dayIndex, totalDays - 1));

      const dropDate = new Date(rangeStart);
      dropDate.setDate(dropDate.getDate() + clampedDayIndex);

      // Don't allow drops on past dates
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return dropDate >= today;
    },
    drop: (item: Job, monitor) => {
      const offset = monitor.getClientOffset();
      if (!offset || !timelineRef.current) return;

      // Get timeline element bounds
      const timelineBounds = timelineRef.current.getBoundingClientRect();

      // Calculate X position relative to timeline start (after the 200px resource column)
      const relativeX = offset.x - timelineBounds.left;

      // Calculate which day was dropped on
      const dayIndex = Math.floor(relativeX / pixelsPerDay);
      const clampedDayIndex = Math.max(0, Math.min(dayIndex, totalDays - 1));

      // Calculate the actual date
      const dropDate = new Date(rangeStart);
      dropDate.setDate(dropDate.getDate() + clampedDayIndex);

      onDrop(item, resourceId, dropDate);

      // Clear hover state
      setHoverDayIndex(null);
      setHoverJob(null);
    },
    hover: (item: Job, monitor) => {
      const offset = monitor.getClientOffset();
      if (!offset || !timelineRef.current) {
        setHoverDayIndex(null);
        setHoverJob(null);
        return;
      }

      // Get timeline element bounds
      const timelineBounds = timelineRef.current.getBoundingClientRect();

      // Calculate X position relative to timeline start
      const relativeX = offset.x - timelineBounds.left;

      // Calculate which day is being hovered
      const dayIndex = Math.floor(relativeX / pixelsPerDay);
      const clampedDayIndex = Math.max(0, Math.min(dayIndex, totalDays - 1));

      setHoverDayIndex(clampedDayIndex);
      setHoverJob(item);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  // Calculate job bar positions with vertical stacking for overlaps
  const jobBars = jobs
    .map((job) => {
      if (!job.scheduledDate) return null;

      // For hour view, use startTime and endTime if available
      if (zoomLevel === 'hour' && job.startTime && job.endTime) {
        const jobStart = parseISO(job.startTime);
        const jobEnd = parseISO(job.endTime);

        // Calculate hours from range start
        const msFromStart = jobStart.getTime() - rangeStart.getTime();
        const hoursFromStart = msFromStart / (1000 * 60 * 60);

        const jobDurationMs = jobEnd.getTime() - jobStart.getTime();
        const jobDurationHours = jobDurationMs / (1000 * 60 * 60);

        // Skip if job is outside visible range
        const totalHours = totalDays * 24;
        if (hoursFromStart + jobDurationHours < 0 || hoursFromStart > totalHours) {
          return null;
        }

        const pixelsPerHour = pixelsPerDay / 24;
        const left = Math.max(0, hoursFromStart * pixelsPerHour);
        const width = Math.max(pixelsPerHour * 0.5, jobDurationHours * pixelsPerHour); // Minimum 30min width

        return {
          job,
          left,
          width,
          startDay: hoursFromStart / 24,
          endDay: (hoursFromStart + jobDurationHours) / 24,
        };
      }

      // Default: day-based positioning
      const jobStart = parseISO(job.scheduledDate);
      const jobEnd = job.scheduledEndDate ? parseISO(job.scheduledEndDate) : jobStart;

      // Calculate position from rangeStart
      const daysFromStart = differenceInDays(jobStart, rangeStart);
      const jobDuration = differenceInDays(jobEnd, jobStart) + 1; // +1 to include end day

      // Skip if job is outside visible range
      if (daysFromStart + jobDuration < 0 || daysFromStart > totalDays) {
        return null;
      }

      const left = Math.max(0, daysFromStart * pixelsPerDay);
      const width = jobDuration * pixelsPerDay;

      return {
        job,
        left,
        width,
        startDay: daysFromStart,
        endDay: daysFromStart + jobDuration,
      };
    })
    .filter(Boolean);

  // Assign vertical lanes to avoid overlaps
  const lanes: { startDay: number; endDay: number }[][] = [];
  const jobsWithLanes = jobBars
    .map((bar) => {
      if (!bar) return null;

      // Find which lane this job should go in
      let lane = 0;

      // Check each existing lane for overlap
      for (let l = 0; l < 10; l++) {
        // Max 10 lanes
        if (!lanes[l]) lanes[l] = [];

        const hasOverlap = lanes[l].some(
          (occupiedJob) => bar.startDay < occupiedJob.endDay && bar.endDay > occupiedJob.startDay,
        );

        if (!hasOverlap) {
          lane = l;
          lanes[l].push({ startDay: bar.startDay, endDay: bar.endDay });
          break;
        }
      }

      return {
        ...bar,
        lane,
      };
    })
    .filter(Boolean);

  // Calculate total lanes needed (row height)
  const maxLane =
    jobsWithLanes.length > 0 ? Math.max(...jobsWithLanes.map((b) => b?.lane ?? 0)) : 0;
  const rowHeight = Math.max(60, (maxLane + 1) * 56 + 8); // 56px per lane + 8px padding

  return (
    <div
      ref={drop}
      className={`flex border-b border-border transition-colors ${
        isOver && canDrop ? 'bg-blue-50' : isOver ? 'bg-red-50' : 'hover:bg-muted/20'
      }`}
      style={{ minHeight: `${rowHeight}px` }}
      onMouseLeave={() => {
        // Clear hover state when mouse leaves row
        setHoverDayIndex(null);
        setHoverJob(null);
      }}
    >
      {/* Team name (sticky left column) */}
      <div className="w-[200px] flex-shrink-0 border-r-2 border-border px-4 py-3 bg-card flex items-center sticky left-0 z-30">
        <div>
          <div className="font-semibold text-sm">{resourceName}</div>
          <div className="text-xs text-muted-foreground mt-0.5">{jobs.length} jobs</div>
        </div>
      </div>

      {/* Timeline area with job bars */}
      <div
        ref={timelineRef}
        className="flex-1 relative overflow-hidden"
        style={{
          width: `${totalDays * pixelsPerDay}px`,
          minHeight: `${rowHeight}px`,
        }}
      >
        {/* Empty state hint for rows with no jobs */}
        {jobs.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="text-xs text-muted-foreground">No jobs scheduled</span>
          </div>
        )}

        {/* Day grid lines and capacity visualization */}
        {Array.from({ length: totalDays }).map((_, i) => {
          const date = new Date(rangeStart);
          date.setDate(date.getDate() + i);
          const isWeekend = date.getDay() === 0 || date.getDay() === 6;
          const dateStr = format(date, 'yyyy-MM-dd');

          // Check if date is in the past
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const isPast = date < today;

          // Get capacity for this day
          const capacity = dailyCapacity?.get(dateStr);
          const utilizationPercent = capacity?.utilizationPercent || 0;
          const isOverallocated = capacity?.isOverallocated || false;
          const hasConflicts = conflicts?.has(dateStr) || false;

          // Capacity bar color based on utilization
          let capacityColor = 'bg-green-500/70'; // Under capacity
          let bgHighlight = '';
          if (utilizationPercent > 100 || isOverallocated) {
            capacityColor = 'bg-red-500/70'; // Over capacity (>100%)
            bgHighlight = 'bg-red-50/30 dark:bg-red-950/20';
          } else if (utilizationPercent >= 90) {
            capacityColor = 'bg-yellow-500/70'; // Near capacity (90-100%)
            bgHighlight = 'bg-yellow-50/20 dark:bg-yellow-950/10';
          }

          return (
            <div
              key={i}
              className={`absolute top-0 bottom-0 border-r border-border/30 ${
                isPast
                  ? 'bg-muted/40 opacity-60'
                  : isWeekend
                    ? 'bg-slate-100/60 dark:bg-slate-800/40'
                    : bgHighlight
              }`}
              style={{
                left: `${i * pixelsPerDay}px`,
                width: `${pixelsPerDay}px`,
              }}
            >
              {/* Overallocation warning icon - only show when actually over capacity */}
              {isOverallocated && (
                <div className="absolute top-1 right-1 z-40">
                  <AlertTriangle
                    className="w-4 h-4 text-red-600 dark:text-red-400"
                    title="Over-allocated - exceeds team capacity"
                  />
                </div>
              )}

              {/* Capacity bar (bottom of cell) */}
              {capacity && utilizationPercent > 0 && (
                <div
                  className={`absolute bottom-0 left-0 right-0 ${capacityColor} transition-all`}
                  style={{
                    height: `${Math.min(utilizationPercent, 100)}%`,
                    maxHeight: '100%',
                  }}
                  title={`${utilizationPercent.toFixed(1)}% utilized (${capacity.totalHours.toFixed(1)}h / ${capacity.availableHours}h)${isOverallocated ? ' - OVER CAPACITY' : ''}`}
                />
              )}
            </div>
          );
        })}

        {/* Job bars with vertical stacking */}
        {jobsWithLanes.map((bar, index) => {
          if (!bar) return null;
          const topPosition = 4 + bar.lane * 56; // 4px top padding + 56px per lane
          return (
            <div
              key={`${bar.job.id}-${bar.lane}-${index}`}
              className="absolute"
              style={{
                left: `${bar.left}px`,
                top: `${topPosition}px`,
                width: `${bar.width}px`,
              }}
            >
              <GanttJobBar job={bar.job} left={0} width={bar.width} onJobClick={onJobClick} />
            </div>
          );
        })}

        {/* Drop indicator - shows where job will land */}
        {isOver && hoverDayIndex !== null && hoverJob && (
          <div
            className={`absolute top-1 h-12 border-2 border-dashed pointer-events-none rounded ${
              canDrop ? 'border-blue-500 bg-blue-100/30' : 'border-red-500 bg-red-100/30'
            }`}
            style={{
              left: `${hoverDayIndex * pixelsPerDay}px`,
              width: `${Math.max(
                (hoverJob.duration || hoverJob.estimatedHours || 1) * (pixelsPerDay / 8),
                pixelsPerDay,
              )}px`,
            }}
          >
            <div className="absolute inset-0 flex items-center justify-center">
              <span
                className={`text-xs font-semibold px-2 py-0.5 rounded ${
                  canDrop ? 'text-blue-700 bg-white/80' : 'text-red-700 bg-white/80'
                }`}
              >
                {canDrop
                  ? format(addDays(rangeStart, hoverDayIndex), 'MMM d')
                  : 'Cannot schedule in past'}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
